<script setup lang="ts">
import { onMounted, watch } from 'vue';
import { RouterView } from 'vue-router';
import Toast from 'primevue/toast';
import ConfirmDialog from 'primevue/confirmdialog';
import { initializeFirebase } from '@/config/firebase';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import SkipLink from '@/components/SkipLink.vue';
import ScreenReaderAnnouncer from '@/components/ScreenReaderAnnouncer.vue';
import ErrorBoundary from '@/components/ErrorBoundary.vue';
import GlobalErrorHandler from '@/components/GlobalErrorHandler.vue';

const authStore = useAuthStore();
const orgStore = useOrganizationStore();

function handleAppError(error: Error, info: string) {
  console.error('App-level error:', error, info);
}

// Fetch organizations once auth is ready
watch(() => authStore.isAuthenticated, async (isAuth) => {
  if (isAuth) {
    try {
      await orgStore.fetchOrganizations();
      orgStore.restoreSelection();
      if (orgStore.currentOrganization) {
        await orgStore.fetchProducts();
        orgStore.restoreSelection();
      }
    } catch (e) {
      console.error('Failed to load organizations:', e);
    }
  }
}, { immediate: true });

onMounted(() => {
  initializeFirebase();
  authStore.initAuthListener();
});
</script>

<template>
  <!-- Skip link for keyboard users -->
  <SkipLink />

  <!-- Screen reader announcer for dynamic content -->
  <ScreenReaderAnnouncer />

  <Toast />
  <ConfirmDialog />

  <!-- Global error handler for unhandled errors -->
  <GlobalErrorHandler />

  <!-- Error boundary for component-level errors -->
  <ErrorBoundary @error="handleAppError">
    <RouterView v-if="authStore.initialized || !authStore.loading" />
    <div
      v-else
      class="flex min-h-screen items-center justify-center bg-gray-100"
      role="status"
      aria-label="Loading application"
    >
      <div class="text-center">
        <i class="pi pi-spin pi-spinner text-4xl text-primary" aria-hidden="true"></i>
        <p class="mt-4 text-gray-600">Loading...</p>
      </div>
    </div>
  </ErrorBoundary>
</template>

<style scoped></style>
